import {css} from '@emotion/css';
import cx from 'clsx';
import {isString} from 'lodash/fp';
import {cloneElement, CSSProperties, ReactElement} from 'react';

type ChildProps = {
  className?: string;
  style?: CSSProperties;
  children?: any;
  title?: string;
};

export type LineClampProps = {
  /**
   * Should be Typo, or p, span, i, strong, etc
   */
  children: ReactElement<ChildProps>;
  /**
   * Number of lines that will be clamped
   */
  lineClamp?: number | string;
  style?: CSSProperties;
  title?: string;
};

const sSingleLineClamp = css`
  && {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-break: break-all;
  }
`;

const sMultiLineClamp = css`
  && {
    /* @see https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp */
    @supports (-webkit-line-clamp: 2) {
      word-break: break-word;
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: var(--line-clamp);
      -webkit-box-orient: vertical;
    }

    @supports not (-webkit-line-clamp: 2) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      word-break: break-all;
    }
  }
`;

/**
 * Typo/Typography on steroids
 * @constructor
 */
export default function LineClamp({children, title, lineClamp = 1, style = {}}: LineClampProps) {
  return cloneElement(children, {
    className: cx(children.props.className, {
      [sSingleLineClamp]: lineClamp === 1,
      [sMultiLineClamp]: +lineClamp > 1,
    }),
    style: {
      '--line-clamp': lineClamp || 'inherit',
      ...(children.props.style || {}),
      ...style,
    } as CSSProperties,
    title: title || (isString(children.props.children) ? children.props.children : undefined),
  });
}
